/**
 * Authentication Context Provider
 *
 * Manages authentication state with Google OAuth2, password login, and the
 * credential+TOTP (ADR-008/009) flow the Mission Trust advisor uses. The session
 * lives in an HttpOnly cookie the backend sets on login/totp/exchange/refresh; the
 * SPA holds NO bearer token in browser storage (SEC-V04 / GH #1780). On load the
 * provider rehydrates the user with GET /api/v1/auth/me (credentials:'include').
 *
 * Credential+TOTP (M-PILOT-03 WAVE 5): the advisor is TOTP-enrolled, so
 * POST /api/v1/auth/login returns an intermediate (scope=totp-challenge) token
 * rather than a direct access token. login() surfaces { totpRequired:true }; the
 * caller then collects a 6-digit code and calls submitTotp(code), which completes
 * POST /api/v1/auth/totp/challenge and yields the access token (aud=aaim-platform,
 * mfaVerified=true) the connector guard requires. The backend ALSO sets the session
 * cookie on each of these responses; the SPA never persists the body token.
 *
 * Step-up (stepUp): the connector-attest write (and the connector queue read)
 * require a token minted within the last 15 minutes (the guard's step-up freshness
 * window). stepUp(code) re-runs the credential+TOTP challenge in place -- using the
 * in-memory credentials captured at login -- to re-mint a fresh token immediately
 * before the attest POST, without losing the advisor's keystroked form state. The
 * fresh re-mint is delivered ONLY as the HttpOnly session cookie by the backend
 * (browser-token-storage-hardening bts-02b / GH #1806: no body bearer is read or
 * returned). The attest POST then authenticates via that same-origin session cookie
 * (credentials:'include'); the connector guard reads the cookie + checks 15-minute
 * freshness from the token's iat claim. stepUp returns void.
 * The platform /step-up-token endpoint is FI-staff-operation-bound with a TOTP stub
 * (M-PILOT-03 WAVE-0 risk probe), so the credential+TOTP re-challenge is the pilot
 * step-up equivalent. The credentials are held in React memory only (never
 * localStorage) and cleared on logout.
 */
import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { csrfHeader } from '../api/client';

// Empty-string fallback per F-VITE-AUTH-LOCALHOST-DEFAULT-FEDERATED-SPAS.
// Missing build-arg produces a relative-URL fetch that 404s visibly on the SPA's
// own origin rather than silently issuing CORS requests to a user's localhost.
const AUTH_SERVICE_URL = import.meta.env.VITE_AUTH_SERVICE_URL ?? '';
// Legacy localStorage key from the pre-cookie session. No longer written; cleared
// once on mount so a stale bearer token does not linger in browser storage.
const LEGACY_STORAGE_KEY = 'aaim-advisor-tools-auth';

interface User {
  id: string;
  email: string;
  name: string;
  firstName?: string;
  lastName?: string;
  roles?: string[];
  permissions?: string[];
  institutionId?: string;
  tenantId?: string;
  role?: 'ADVISOR' | 'ADMIN';
  // Whether the session's second factor is satisfied. Surfaced on the login /
  // totp response user object so the connector step-up path is chosen from the
  // user object, not by decoding the access token (which is HttpOnly-cookie-only).
  mfaVerified?: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ totpRequired: boolean }>;
  submitTotp: (code: string) => Promise<void>;
  stepUp: (code: string) => Promise<void>;
  canStepUp: boolean;
  // True for MFA-off sessions: the connector attest step-up needs no TOTP code.
  mfaOff: boolean;
  loginWithGoogle: () => Promise<void>;
  // OAuth completion: POST the ?code= to /api/v1/auth/exchange (sets the cookie),
  // then rehydrate the user from /api/v1/auth/me. No token touches the SPA.
  completeOAuth: (code: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  oauthLoading: boolean;
  oauthError: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Normalize a user payload to a User. Accepts the `/api/v1/auth/me` shape
 * (`{ user }` or the user object directly) and the inline `user` object the
 * login / totp-challenge responses now carry. No access token is decoded -- the
 * identity comes from the response user object (the token is HttpOnly-cookie-only).
 */
function userFromMe(payload: unknown): User | null {
  const root = (payload ?? {}) as Record<string, unknown>;
  const candidate = (root.user ?? root) as Record<string, unknown>;
  const id = typeof candidate.id === 'string' ? candidate.id : '';
  const email = typeof candidate.email === 'string' ? candidate.email : '';
  if (!id) return null;
  return {
    id,
    email,
    name: typeof candidate.name === 'string' ? candidate.name : email,
    firstName: typeof candidate.firstName === 'string' ? candidate.firstName : undefined,
    lastName: typeof candidate.lastName === 'string' ? candidate.lastName : undefined,
    roles: Array.isArray(candidate.roles) ? (candidate.roles as string[]) : undefined,
    permissions: Array.isArray(candidate.permissions) ? (candidate.permissions as string[]) : undefined,
    institutionId: typeof candidate.institutionId === 'string' ? candidate.institutionId : undefined,
    tenantId: typeof candidate.tenantId === 'string' ? candidate.tenantId : undefined,
    role: candidate.role === 'ADMIN' || candidate.role === 'ADVISOR' ? candidate.role : undefined,
    mfaVerified: typeof candidate.mfaVerified === 'boolean' ? candidate.mfaVerified : undefined,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);
  // In-memory only -- never persisted. Captured at login for the connector step-up
  // re-challenge; cleared on logout.
  const [pendingTotp, setPendingTotp] = useState<{ intermediateToken: string; email: string; password: string } | null>(null);
  const [creds, setCreds] = useState<{ email: string; password: string } | null>(null);
  const [mfaOff, setMfaOff] = useState(false);

  // Apply an authenticated session to React state. The bearer token is NOT stored
  // (it lives in the HttpOnly cookie the backend set on the same response).
  const applySession = useCallback((nextUser: User, sessionMfaOff: boolean) => {
    setUser(nextUser);
    setMfaOff(sessionMfaOff);
  }, []);

  // Rehydrate the current user from the session cookie. Returns true if a session
  // is present. No token is read from storage -- the cookie carries the session.
  const hydrateFromMe = useCallback(async (): Promise<boolean> => {
    try {
      const res = await fetch(`${AUTH_SERVICE_URL}/api/v1/auth/me`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) {
        setUser(null);
        return false;
      }
      const meUser = userFromMe(await res.json());
      if (!meUser) {
        setUser(null);
        return false;
      }
      setUser(meUser);
      return true;
    } catch {
      setUser(null);
      return false;
    }
  }, []);

  useEffect(() => {
    // One-time cleanup of the legacy pre-cookie token blob so no bearer lingers
    // in localStorage.
    try {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      // ignore -- storage unavailable
    }
    let cancelled = false;
    void (async () => {
      await hydrateFromMe();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [hydrateFromMe]);

  const login = useCallback(async (email: string, password: string): Promise<{ totpRequired: boolean }> => {
    // Canonical aaim-core path: POST /api/v1/auth/login (same-origin -> nginx /api/v1/auth
    // -> aaim-core-authorization). For a TOTP-enrolled advisor this returns an
    // intermediate token (scope=totp-challenge); for a non-TOTP user it returns the
    // access token directly. credentials:'include' so the Set-Cookie session lands.
    const response = await fetch('/api/v1/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(`Login failed (${response.status}): ${errorText || response.statusText}`);
    }

    const data = (await response.json()) as {
      success?: boolean;
      user?: User & { tenantId?: string };
      accessToken?: string;
      refreshToken?: string;
      intermediateToken?: string;
      scope?: string;
      totpRequired?: boolean;
      totpEnrollmentRequired?: boolean;
    };

    // Two-step (TOTP) path: stash the intermediate token + credentials, signal the caller.
    if (data.intermediateToken && !data.accessToken) {
      setPendingTotp({ intermediateToken: data.intermediateToken, email, password });
      return { totpRequired: true };
    }

    // Direct path (non-TOTP user). The session cookie is already set by the backend;
    // the connector attest step-up re-mints codelessly for an MFA-disabled tenant when
    // the session is mfaVerified. Read mfaVerified from the response user object, NOT
    // by decoding the access token -- the token is delivered as an HttpOnly session
    // cookie and is stripped from the body (browser-token-storage-hardening N3b).
    if (data.user) {
      const directMfaOff = data.user.mfaVerified === true;
      applySession(data.user, directMfaOff);
      setCreds({ email, password });
      return { totpRequired: false };
    }

    throw new Error('Login response did not include a user or intermediate token');
  }, [applySession]);

  const submitTotp = useCallback(async (code: string): Promise<void> => {
    if (!pendingTotp) {
      throw new Error('No pending TOTP challenge -- start the sign-in again');
    }
    const response = await fetch('/api/v1/auth/totp/challenge', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${pendingTotp.intermediateToken}` },
      credentials: 'include',
      body: JSON.stringify({ code }),
    });
    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(`TOTP verification failed (${response.status}): ${errorText || response.statusText}`);
    }
    const data = (await response.json()) as { user?: Record<string, unknown> };
    // The session cookie is set by the backend; establish the user identity from the
    // response user object (the access token is delivered as an HttpOnly session
    // cookie, not a body-readable bearer).
    const nextUser = userFromMe(data.user);
    if (!nextUser) {
      throw new Error('TOTP challenge did not return a user');
    }
    // TOTP-completed session: the attest step-up re-asserts the authenticator.
    applySession(nextUser, false);
    setCreds({ email: pendingTotp.email, password: pendingTotp.password });
    setPendingTotp(null);
  }, [pendingTotp, applySession]);

  // Re-mint a fresh aud=aaim-platform, mfaVerified session via an in-place credential+TOTP
  // re-challenge. The fresh token is delivered ONLY as the HttpOnly session cookie by the
  // backend (browser-token-storage-hardening bts-02b / GH #1806: no body bearer is read or
  // returned). The connector attest then authenticates via that fresh same-origin cookie;
  // the guard reads the cookie + checks 15-minute freshness from the token's iat claim.
  const stepUp = useCallback(async (code: string): Promise<void> => {
    if (!creds) {
      throw new Error('Session credentials unavailable -- sign in again to attest');
    }
    const loginRes = await fetch('/api/v1/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email: creds.email, password: creds.password }),
    });
    if (!loginRes.ok) {
      throw new Error(`Step-up re-authentication failed (${loginRes.status})`);
    }
    // The intermediate (scope=totp-challenge) token is the only body field read here; it
    // is NOT a session bearer (it cannot authenticate the attest) and is kept in the body.
    // The full access/refresh tokens are delivered as HttpOnly cookies and stripped from
    // the body (N3b), so we never read them.
    const loginData = (await loginRes.json()) as { intermediateToken?: string };
    // Direct (non-TOTP) re-login: the full session cookie is already set by the backend.
    if (!loginData.intermediateToken) {
      return;
    }
    // TOTP path: complete the challenge; the backend sets the fresh full-session cookie.
    const chRes = await fetch('/api/v1/auth/totp/challenge', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${loginData.intermediateToken}` },
      credentials: 'include',
      body: JSON.stringify({ code }),
    });
    if (!chRes.ok) {
      const errorText = await chRes.text().catch(() => '');
      throw new Error(`Step-up TOTP verification failed (${chRes.status}): ${errorText || chRes.statusText}`);
    }
  }, [creds]);

  const loginWithGoogle = useCallback(async () => {
    setOauthLoading(true);
    setOauthError(null);
    try {
      const response = await fetch(`${AUTH_SERVICE_URL}/api/v1/auth/oauth2/authorize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          provider: 'google',
          callbackUrl: `${window.location.origin}/auth/callback`,
        }),
      });
      if (!response.ok) {
        const errorData = (await response.json().catch(() => null)) as { error?: { message?: string } } | null;
        throw new Error(errorData?.error?.message ?? `Authorization request failed (${response.status})`);
      }
      const data = (await response.json()) as { authorizationUrl: string; state: string };
      sessionStorage.setItem('oauth_state', data.state);
      window.location.href = data.authorizationUrl;
    } catch (error) {
      setOauthLoading(false);
      setOauthError(error instanceof Error ? error.message : 'OAuth login failed');
    }
  }, []);

  // Complete the OAuth2 authorization-code flow. The aaim-core callback redirects back
  // with ?code= on the query string; exchange it for the session cookie, then rehydrate
  // the user from /api/v1/auth/me. No token is read from the URL or written to storage.
  const completeOAuth = useCallback(async (code: string): Promise<void> => {
    const res = await fetch(`${AUTH_SERVICE_URL}/api/v1/auth/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ code }),
    });
    if (!res.ok) {
      const errorText = await res.text().catch(() => '');
      throw new Error(`OAuth code exchange failed (${res.status}): ${errorText || res.statusText}`);
    }
    const ok = await hydrateFromMe();
    if (!ok) {
      throw new Error('Session could not be established after sign-in');
    }
    setOauthLoading(false);
    setOauthError(null);
  }, [hydrateFromMe]);

  const logout = useCallback(() => {
    // Best-effort: tell the backend to clear the HttpOnly session cookies.
    // CSRF double-submit (N8): logout is a cookie-authenticated mutation, so echo
    // the csrf-token cookie as X-CSRF-Token (no-op when the cookie is absent). The
    // login/totp/exchange/oauth2 POSTs are pre-session bootstrap or Bearer-token
    // authenticated, so CSRF double-submit does not apply to them.
    void fetch(`${AUTH_SERVICE_URL}/api/v1/auth/logout`, {
      method: 'POST',
      headers: { ...csrfHeader('POST') },
      credentials: 'include',
    }).catch(() => {
      // ignore -- local state is cleared regardless
    });
    try {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      // ignore
    }
    setUser(null);
    setCreds(null);
    setPendingTotp(null);
    setOauthLoading(false);
    setOauthError(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        submitTotp,
        stepUp,
        canStepUp: creds !== null,
        mfaOff,
        loginWithGoogle,
        completeOAuth,
        logout,
        isAuthenticated: !!user,
        oauthLoading,
        oauthError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
